import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBed,
  FaUserInjured,
  FaTools,
  FaUserMd,
  FaUserNurse,
  FaHeartbeat,
  FaStethoscope,
  FaPills,
  FaTasks,
  FaMale,
  FaFemale,
  FaChild,
} from "react-icons/fa";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import TransitStatus from "./TransitStatus";

const wards = [
  {
    id: 1,
    number: "Ward 101",
    capacity: 5,
    occupied: 3,
    status: "Available",
    maintenance: 1,
    staff: {
      doctors: 2,
      nurses: 4,
    },
    demographics: {
      male: 2,
      female: 1,
      children: 0,
    },
    beds: [
      {
        bedNumber: "B101-1",
        patient: {
          name: "Arjun Sharma",
          age: 45,
          condition: "Recovering from Surgery",
          procedure: "Wound Dressing",
          vitals: {
            heartRate: 82,
            bloodPressure: "118/80",
            oxygenSaturation: "98%",
            lastChecked: "Today at 9:30 AM",
          },
          medications: ["Paracetamol", "Amoxicillin"],
          requests: ["Physiotherapy Appointment", "Special Diet"],
        },
        transitStatus: 2,
      },
      {
        bedNumber: "B101-2",
        patient: {
          name: "Priya Gupta",
          age: 50,
          condition: "Post-COVID Recovery",
          procedure: "Pulmonary Function Test",
          vitals: {
            heartRate: 78,
            bloodPressure: "120/82",
            oxygenSaturation: "96%",
            lastChecked: "Today at 10:00 AM",
          },
          medications: ["Vitamin C", "Lung Care Syrup"],
          requests: [],
        },
        transitStatus: 3,
      },
      {
        bedNumber: "B101-3",
        patient: {
          name: "Ravi Kumar",
          age: 60,
          condition: "Diabetic Care",
          procedure: "Insulin Dose Adjustment",
          vitals: {
            heartRate: 85,
            bloodPressure: "140/90",
            oxygenSaturation: "95%",
            lastChecked: "Today at 8:45 AM",
          },
          medications: ["Insulin", "Metformin"],
          requests: ["Blood Sugar Test", "Foot Examination"],
        },
        transitStatus: 1,
      },
    ],
  },
];

const statusColors = {
  Available: "bg-green-500",
  Full: "bg-red-500",
  Maintenance: "bg-yellow-500",
};

export default function WardDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ward = wards.find((ward) => ward.id === parseInt(id));

  if (!ward) {
    return (
      <div className="p-6 bg-gray-100 min-h-screen flex justify-center items-center">
        <h1 className="text-2xl font-bold text-red-600">Ward Not Found</h1>
      </div>
    );
  }

  const occupancyRate = Math.floor((ward.occupied / ward.capacity) * 100);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <button
          onClick={() => navigate("/new/ipd")}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
        >
          <FaArrowLeft />
          Back to List
        </button>
        <h1 className="text-3xl font-bold text-gray-800">
          {ward.number} Dashboard
        </h1>
      </div>

      {/* Occupancy and Demographics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">
            Occupancy Overview
          </h2>
          <div className="w-40 h-40 mb-6">
            <CircularProgressbar
              value={occupancyRate}
              text={`${occupancyRate}%`}
              styles={buildStyles({
                textSize: "18px",
                pathColor: ward.status === "Available" ? "#22c55e" : "#f87171",
                textColor: "#374151",
                trailColor: "#e5e7eb",
              })}
            />
          </div>
          <div className="grid grid-cols-3 gap-4 w-full text-center">
            <div>
              <FaBed className="inline text-blue-500 mr-1" />
              <p className="text-sm text-gray-500">Total Beds</p>
              <p className="text-xl font-bold text-gray-800">{ward.capacity}</p>
            </div>
            <div>
              <FaUserInjured className="inline text-red-500 mr-1" />
              <p className="text-sm text-gray-500">Occupied Beds</p>
              <p className="text-xl font-bold text-gray-800">{ward.occupied}</p>
            </div>
            <div>
              <FaTools className="inline text-yellow-500 mr-1" />
              <p className="text-sm text-gray-500">Maintenance Beds</p>
              <p className="text-xl font-bold text-gray-800">
                {ward.maintenance}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">
            Ward Demographics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Male Demographics */}
            <div className="flex flex-col items-center bg-gray-50 p-4 rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mb-2">
                <CircularProgressbar
                  value={(ward.demographics.male / ward.capacity) * 100}
                  text={`${Math.round((ward.demographics.male / ward.capacity) * 100)}%`}
                  styles={buildStyles({
                    textSize: "12px",
                    pathColor: "#2563eb", // Blue for Male
                    textColor: "#374151",
                    trailColor: "#d1d5db",
                  })}
                />
              </div>
              <FaMale className="text-blue-500 text-2xl mb-1" />
              <p className="text-base font-medium text-gray-600">Male</p>
              <p className="text-2xl font-bold text-gray-800">
                {ward.demographics.male}
              </p>
            </div>

            {/* Female Demographics */}
            <div className="flex flex-col items-center bg-gray-50 p-4 rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mb-2">
                <CircularProgressbar
                  value={(ward.demographics.female / ward.capacity) * 100}
                  text={`${Math.round((ward.demographics.female / ward.capacity) * 100)}%`}
                  styles={buildStyles({
                    textSize: "12px",
                    pathColor: "#ec4899", // Pink for Female
                    textColor: "#374151",
                    trailColor: "#d1d5db",
                  })}
                />
              </div>
              <FaFemale className="text-pink-500 text-2xl mb-1" />
              <p className="text-base font-medium text-gray-600">Female</p>
              <p className="text-2xl font-bold text-gray-800">
                {ward.demographics.female}
              </p>
            </div>

            {/* Children Demographics */}
            <div className="flex flex-col items-center bg-gray-50 p-4 rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 mb-2">
                <CircularProgressbar
                  value={(ward.demographics.children / ward.capacity) * 100}
                  text={`${Math.round((ward.demographics.children / ward.capacity) * 100)}%`}
                  styles={buildStyles({
                    textSize: "12px",
                    pathColor: "#f59e0b", // Yellow for Children
                    textColor: "#374151",
                    trailColor: "#d1d5db",
                  })}
                />
              </div>
              <FaChild className="text-yellow-500 text-2xl mb-1" />
              <p className="text-base font-medium text-gray-600">Children</p>
              <p className="text-2xl font-bold text-gray-800">
                {ward.demographics.children}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bed Details */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">
          Bed Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ward.beds.map((bed, index) => (
            <div
              key={index}
              className="bg-gray-50 p-4 rounded-lg shadow-md border border-gray-200"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                {bed.bedNumber}
              </h3>
              {/* Add Transit Status */}
              <TransitStatus currentStage={bed.transitStatus} />
              <p className="text-gray-600">
                <strong>Patient:</strong> {bed.patient.name}, Age:{" "}
                {bed.patient.age}
              </p>
              <p className="text-gray-600">
                <FaHeartbeat className="inline text-red-500 mr-2" />
                <strong>Condition:</strong> {bed.patient.condition}
              </p>
              <p className="text-gray-600">
                <FaStethoscope className="inline text-blue-500 mr-2" />
                <strong>Procedure:</strong> {bed.patient.procedure}
              </p>
              <p className="text-gray-600">
                <strong>Vitals:</strong>
                <ul className="list-disc list-inside">
                  <li>Heart Rate: {bed.patient.vitals.heartRate} bpm</li>
                  <li>Blood Pressure: {bed.patient.vitals.bloodPressure}</li>
                  <li>
                    Oxygen Saturation: {bed.patient.vitals.oxygenSaturation}
                  </li>
                  <li>
                    Last Checked:{" "}
                    <span className="italic">
                      {bed.patient.vitals.lastChecked}
                    </span>
                  </li>
                </ul>
              </p>
              <p className="text-gray-600">
                <FaPills className="inline text-green-500 mr-2" />
                <strong>Medications:</strong>
                <ul className="list-disc list-inside">
                  {bed.patient.medications.map((med, medIndex) => (
                    <li key={medIndex}>{med}</li>
                  ))}
                </ul>
              </p>
              {bed.patient.requests.length > 0 && (
                <p className="text-gray-600">
                  <FaTasks className="inline text-yellow-500 mr-2" />
                  <strong>Requests:</strong>
                  <ul className="list-disc list-inside">
                    {bed.patient.requests.map((req, reqIndex) => (
                      <li key={reqIndex}>{req}</li>
                    ))}
                  </ul>
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
